/** IELTS back office: small pieces shared by the course, passages, grading and progress tabs. */

import { type ContentStatusEnum } from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { CheckCheck, Rocket } from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { isTooClose, TooSimilarAlert } from "./QuestionsTab";
import { useAdminExam } from "./examContext";
import { ErrorList } from "./shared";

/** Review then publish (anti-plagiarism filter on the server): the refusal is shown with its reason. */
export function ValidationPanel({
  status,
  pending,
  reviewError,
  publishError,
  done,
  onReview,
  onPublish,
  id,
}: {
  status: ContentStatusEnum;
  pending: boolean;
  reviewError: unknown;
  publishError: unknown;
  done: boolean;
  onReview: () => void;
  onPublish: () => void;
  id: string;
}) {
  const { t } = useTranslation();
  return (
    <section className="stack ielts-validation" aria-labelledby={id}>
      <h3 id={id} className="ao-headline">
        {t("academyAdmin.validation")}
      </h3>
      <p className="muted">{t("academyAdmin.validationHint")}</p>
      <div className="row">
        <Button variant="secondary" icon={<CheckCheck size={16} strokeWidth={1.75} aria-hidden="true" />} disabled={pending || status !== "draft"} onClick={onReview}>
          {t("academyAdmin.review")}
        </Button>
        <Button icon={<Rocket size={16} strokeWidth={1.75} aria-hidden="true" />} disabled={pending || status === "published"} onClick={onPublish}>
          {t("academyAdmin.publish")}
        </Button>
      </div>
      <TooSimilarAlert error={publishError ?? reviewError} />
      <ErrorList error={reviewError ?? (isTooClose(publishError) ? null : publishError)} />
      {done ? (
        <span role="status" className="ok">
          {t(`academyAdmin.status.${status}`)}
        </span>
      ) : null}
    </section>
  );
}

/** « Listening », « Reading »… (IELTS names of the co / ce / ee / eo codes). */
export function useIeltsSkillName() {
  const { t } = useTranslation();
  const { tef } = useAdminExam(); // D-109: the TEF names its tests in French
  return (skill: string) => t(tef ? `tef.skill.${skill}` : `academyAdmin.ielts.skill.${skill}`, { defaultValue: skill.toUpperCase() });
}

/** A passages text: the TEF's own when there is one (D-109), else the IELTS one. */
export function usePassageText() {
  const { tef } = useAdminExam();
  return (key: string): string | string[] => (tef ? [`tef.admin.passages.${key}`, `academyAdmin.ielts.passages.${key}`] : `academyAdmin.ielts.passages.${key}`);
}

/** **bold** and *italic* of the lesson texts (Markdown subset), as React nodes: never raw HTML. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });
}

/** A lesson explanation: paragraphs, « - » lists, bold and italic. */
export function LessonText({ text, lang }: { text: string; lang: string }) {
  const blocks = text.split(/\n\s*\n/).filter((b) => b.trim());
  return (
    <div lang={lang} className="stack ielts-lesson-text">
      {blocks.map((block, i) => {
        const lines = block.split("\n").filter((l) => l.trim());
        if (lines.every((l) => /^\s*[-•]\s+/.test(l)))
          return (
            <ul key={i} className="ielts-objectives">
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-•]\s+/, ""))}</li>
              ))}
            </ul>
          );
        return (
          <p key={i} className="academy-pre">
            {inline(block)}
          </p>
        );
      })}
    </div>
  );
}
